import { useEffect, useRef, useCallback, useMemo } from 'react';
import useTransactionLog from '../../../../hooks/web3/trade/useTransactionLog';
import TransactionDetail from '../../../../components/TransactionDetail';
import useWidth from '../../../../hooks/useWidth';
import { VariableSizeList as List } from 'react-window';
import Spinner from '../../../../components/Spinner';
import { OrderBookType } from '../../../../config/constants';
import { TransactionLog } from '../../../../context/transaction/transactionTypes';

const ROW_MARGIN = 16;

const TRANSACTION_LOG_HEIGHT = 220;
const TRANSACTION_LOG_HEIGHT_LARGE = 245;
const LABEL_HEIGHT = 17;

const TransactionLogs = () => {
    const windowWidth = useWidth();

    const listRef = useRef<List<any> | null>(null);

    const { transactionLog, isLoading } = useTransactionLog();

    const data = useMemo(
        () =>
            transactionLog
                ? [
                      ...(transactionLog.transactionsToday.length
                          ? ['Today', ...transactionLog.transactionsToday]
                          : []),
                      ...(transactionLog.transactionYesterday.length
                          ? ['Yesterday', ...transactionLog.transactionYesterday]
                          : []),
                      ...(transactionLog.transactionTwoDaysAgo.length
                          ? ['Older', ...transactionLog.transactionTwoDaysAgo]
                          : []),
                  ]
                : [],
        [transactionLog]
    );

    const getHeight = useCallback(
        (index: number) => {
            const labelIndexes = data.reduce((acc, cur, index) => {
                if (typeof cur === 'string') {
                    acc.push(index);
                }

                return acc;
            }, [] as number[]);

            if (labelIndexes.includes(index)) {
                return LABEL_HEIGHT + ROW_MARGIN;
            }

            if (
                [OrderBookType.EXTEND, OrderBookType.PARTIALLY_CLOSE].includes(
                    (data as TransactionLog[])[index].orderType
                )
            ) {
                return TRANSACTION_LOG_HEIGHT_LARGE + ROW_MARGIN;
            }

            return TRANSACTION_LOG_HEIGHT + ROW_MARGIN;
        },
        [data]
    );

    // resets cache of rows
    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [windowWidth, transactionLog]);

    return isLoading ? (
        <div className='loading-container'>
            <Spinner />
            <span>Loading Transaction Log...</span>
        </div>
    ) : data.length ? (
        <List
            width={'100%'}
            ref={listRef}
            height={(TRANSACTION_LOG_HEIGHT + LABEL_HEIGHT) * 2}
            itemCount={data.length}
            itemSize={getHeight}
            className='transaction-log'
        >
            {({ index, style }) => {
                const finalStyle = {
                    ...style,
                    height: (style.height as number) - ROW_MARGIN,
                    width: 'calc(100% - 2rem)',
                    left: '1rem',
                };

                return typeof data[index] === 'string' ? (
                    <span
                        style={{
                            ...style,
                            height: (style.height as number) - ROW_MARGIN,
                            width: 'calc(100% - 2rem)',
                            left: '1rem',
                        }}
                    >
                        {data[index] as string}
                    </span>
                ) : (
                    <TransactionDetail
                        transactionDetail={data[index] as TransactionLog}
                        style={finalStyle}
                    />
                );
            }}
        </List>
    ) : (
        <div className='empty-transaction-log'>You have not made any transactions...</div>
    );
};

export default TransactionLogs;
